import React, { useState, useEffect } from 'react';
import { generateBookingReference } from '@/utils/bookingReferenceGenerator';
import { formatDateInServiceTimezone, getTimezoneLabel } from '@/utils/timezoneUtils';
import { validateCostBreakdown, logValidationErrors, hasOldBookingFeeBug } from '@/utils/validateCostBreakdown';
import { printReceipt } from '@/utils/printReceipt';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Tabs,
  Tab,
  Tooltip,
  Badge,
  FormControl,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Stack,
  Divider,
  Alert,
} from '@mui/material';
import {
  Search,
  FilterList,
  MoreVert,
  DirectionsCar,
  People,
  Assignment,
  Visibility,
  Edit,
  Cancel,
  CheckCircle,
  Schedule,
  Add,
  Download,
  Phone,
  Email,
  FlashOn,
  Refresh,
  PersonAdd,
  DirectionsBoat,
  VerifiedUser as VerificationIcon,
  CameraAlt as CheckinIcon,
  Payment,
  ContentCopy,
  Link as LinkIcon,
  WhatsApp,
  Print,
  Business,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { CreateBookingDialog, CreateBookingData } from '../../components/bookings/CreateBookingDialog';
import { BookingDetailsModal } from '../../components/bookings/BookingDetailsModal';
import { AdminBookingDetailsModal } from '../../components/bookings/AdminBookingDetailsModal';
import { EditBookingModal } from '../../components/bookings/EditBookingModal';
import { bookingService, AdminCreateBookingData, simplBookingService } from '../../services/bookingService';
import { driverService } from '../../services/driverService';
import toast from 'react-hot-toast';
import { getApiBaseUrl } from '@/config/environment';
import { usePermissions } from '@/hooks/usePermissions';

interface Booking {
  id: string;
  dbId?: string; // Actual database ID (might be _id from MongoDB)
  bookingRef: string;
  type: 'rental' | 'driver-hire' | 'agent-assisted';
  bookingType?: 'instant' | 'approval-required';
  serviceCategory?: string; // For filtering: 'vehicle-rental', 'driver-hire', etc.
  customer: {
    name: string;
    phone: string;
    email: string;
    avatar?: string;
  };
  vehicle: {
    make: string;
    model: string;
    year: number;
    plate: string;
    image?: string;
  };
  driver?: {
    name: string;
    rating: number;
    phone: string;
    avatar?: string;
  };
  startDate: string;
  endDate: string;
  serviceLocation?: string;
  timezone?: string;
  status: 'draft' | 'pending' | 'confirmed' | 'active' | 'completed' | 'cancelled';
  amount: number;
  paymentStatus: 'pending' | 'paid' | 'refunded';
  location: string;
  notes?: string;
  comments?: { author: string; text: string; date: string }[];
  timeline?: { event: string; date: string; status: string }[];
  duration?: string;
}

export const EnhancedAllBookingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { isSuperAdmin } = usePermissions();
  const [selectedTab, setSelectedTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [bookingTypeMode, setBookingTypeMode] = useState('all');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [viewDetailsOpen, setViewDetailsOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: 'success' as 'success' | 'error' });
  const [isLoading, setIsLoading] = useState(false);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [createBookingOpen, setCreateBookingOpen] = useState(false);
  const [assignDriverOpen, setAssignDriverOpen] = useState(false);
  const [availableDrivers, setAvailableDrivers] = useState<any[]>([]);
  const [selectedDriverId, setSelectedDriverId] = useState<string>('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [bookingTypeDialogOpen, setBookingTypeDialogOpen] = useState(false);
  const [adminDetailsOpen, setAdminDetailsOpen] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);
  const [modalInitialTab, setModalInitialTab] = useState(0);
  const [paymentLinkDialogOpen, setPaymentLinkDialogOpen] = useState(false);
  const [paymentLinkData, setPaymentLinkData] = useState<{
    paymentUrl: string;
    customerName: string;
    customerEmail: string;
    amount: number;
    isExisting: boolean;
    instructions?: {
      share?: string;
      validity?: string;
      webhook?: string;
    };
  } | null>(null);
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);

  useEffect(() => {
    loadBookings();
  }, [bookingTypeMode]);

  const loadBookings = async () => {
    try {
      setIsLoading(true);

      // Fetch all bookings from the admin endpoint
      const data = await simplBookingService.getAllBookings();


      // Filter client-side based on booking type mode
      let filteredData = data;
      if (bookingTypeMode === 'instant') {
        filteredData = data.filter((b: any) => b.bookingType === 'instant' || b.approvalType === 'instant');
      } else if (bookingTypeMode === 'approval-required') {
        filteredData = data.filter((b: any) => b.bookingType === 'approval-required' || b.approvalType === 'approval_required' || b.approvalType === 'request_to_book');
      }


      // Process the filtered data to match our expected format
      const processedBookings = (filteredData || []).map((booking: any) => {
        return {
          id: booking._id || booking.id, // Prefer _id (database ID) over id (might be reference)
          dbId: booking._id, // Store database ID separately
          bookingRef: booking.bookingRef || booking.id || generateBookingReference(booking._id || booking.id, booking.type as any),
          type: booking.type || 'rental',
          bookingType: booking.bookingType || 'instant',
          serviceCategory: booking.serviceCategory, // Keep serviceCategory for filtering
          // Corporate account data (from nested structure)
          isCorporate: booking.isCorporate || false,
          isCorporateBooking: booking.isCorporate || booking.isCorporateBooking || false,
          corporateAccountId: booking.corporateAccount?.id || booking.corporateAccountId || null,
          companyName: booking.corporateAccount?.companyName || booking.companyName || null,
          corporateAccount: booking.corporateAccount || null,
          customer: {
            name: booking.customer?.name || booking.customer?.fullName || booking.customerName || 'Unknown Customer',
            phone: booking.customer?.phone || booking.customerPhone || 'N/A',
            email: booking.customer?.email || booking.customerEmail || 'N/A'
          },
          vehicle: {
            make: booking.vehicle?.make || 'N/A',
            model: booking.vehicle?.model || 'N/A',
            year: booking.vehicle?.year || new Date().getFullYear(),
            plate: booking.vehicle?.plate || booking.vehicle?.plateNumber || 'N/A'
          },
          driver: booking.driver ? {
            name: booking.driver.name || 'N/A',
            rating: booking.driver.rating || 0,
            phone: booking.driver.phone || 'N/A'
          } : undefined,
          startDate: booking.startDate || booking.createdAt,
          endDate: (() => {
            // Airport transfers don't have separate end dates - use start date
            if (booking.serviceCategory === 'airport-transfer' || booking.serviceType?.toLowerCase().includes('airport')) {
              return booking.startDate || booking.createdAt;
            }
            return booking.endDate || booking.startDate;
          })(),
          status: booking.status || 'pending',
          amount: booking.amount || booking.totalAmount || 0,
          paymentStatus: booking.paymentStatus || booking.payment?.status || 'pending',
          payment: booking.payment, // Include payment object with security deposit data
          securityDeposit: booking.securityDeposit || booking.payment?.securityDeposit, // Fallback for flat structure
          securityDepositStatus: booking.securityDepositStatus || booking.payment?.securityDepositStatus,
          location: booking.location || 'N/A',
          notes: booking.notes || booking.specialRequests?.join(', ') || '',
          duration: (() => {
            // Airport transfers are single point-in-time services, not multi-day rentals
            if (booking.serviceCategory === 'airport-transfer' || booking.serviceType?.toLowerCase().includes('airport')) {
              return 'Same day';
            }
            return booking.duration || 'N/A';
          })(),
          comments: [],
          timeline: [
            { event: 'Booking Created', date: booking.createdAt || new Date().toISOString(), status: 'completed' }
          ]
        };
      });

      setBookings(processedBookings);
    } catch (error) {
      console.error('Failed to load bookings:', error);
      setBookings([]);
      setSnackbar({ 
        open: true, 
        message: 'Failed to load bookings. Please check your connection and try again.', 
        type: 'error' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, booking: Booking) => {
    setAnchorEl(event.currentTarget);
    setSelectedBooking(booking);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    // Don't clear selectedBooking here - it's needed by the modals
    // selectedBooking will be cleared when each modal closes
  };

  const handleViewDetails = (booking: Booking) => {
    setSelectedBooking(booking);
    setViewDetailsOpen(true);
    handleMenuClose();
  };

  const handleCloseDetails = () => {
    setViewDetailsOpen(false);
    setSelectedBooking(null);
  };

  const handleEditBooking = () => {
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleCancelBooking = () => {
    setCancelDialogOpen(true);
    handleMenuClose();
  };

  const handleGeneratePaymentLink = async () => {
    if (!selectedBooking) return;

    handleMenuClose();

    // Check if booking is already paid
    if (selectedBooking.paymentStatus === 'paid') {
      toast.error('This booking has already been paid. No payment link needed.', { duration: 5000 });
      return;
    }

    // Check if booking is cancelled
    if (selectedBooking.status === 'cancelled') {
      toast.error('Cannot generate payment link for cancelled booking.', { duration: 5000 });
      return;
    }

    try {
      const bookingId = selectedBooking.dbId || selectedBooking.id;

      const loadingToast = toast.loading('Generating payment link...');

      // Call dedicated payment link generation endpoint
      const response = await fetch(`${getApiBaseUrl()}/api/admin/bookings/${bookingId}/generate-payment-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          callbackUrl: `${window.location.origin}/payment-success`
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Backend error response:', errorData);
        // Include details if available (e.g., "Duplicate Transaction Reference")
        const errorMessage = errorData.error?.details || errorData.error?.message || errorData.message || 'Failed to generate payment link';
        toast.dismiss(loadingToast);
        throw new Error(errorMessage);
      }

      const result = await response.json();

      const { paymentUrl, customerEmail, customerName, amount, instructions, isExisting } = result.data;

      // Dismiss loading toast
      toast.dismiss(loadingToast);

      // Copy to clipboard
      await navigator.clipboard.writeText(paymentUrl);

      // Store data and show modal
      setPaymentLinkData({
        paymentUrl,
        customerName,
        customerEmail,
        amount,
        isExisting,
        instructions
      });
      setPaymentLinkDialogOpen(true);

      const toastMessage = isExisting ? 'Existing payment link retrieved!' : 'New payment link created!';
      toast.success(toastMessage, { duration: 3000 });

      // Refresh bookings to show updated status
      await loadBookings();

    } catch (error: any) {
      console.error('❌ Error generating payment link:', error);

      // Show user-friendly error messages
      let errorMessage = 'Failed to generate payment link';

      if (error.message.includes('ALREADY_PAID')) {
        errorMessage = 'This booking has already been paid';
      } else if (error.message.includes('BOOKING_CANCELLED')) {
        errorMessage = 'Cannot generate payment link for cancelled booking';
      } else if (error.message.includes('MISSING_EMAIL')) {
        errorMessage = 'Customer email is required to generate payment link';
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage, { duration: 5000 });
    }
  };

  const handlePrintReceipt = () => {
    if (!selectedBooking) return;
    setReceiptDialogOpen(true);
    handleMenuClose();
  };

  const handlePrint = () => {
    if (!selectedBooking) return;

    try {
      printReceipt(selectedBooking as any);
    } catch (error) {
      console.error('Error printing receipt:', error);
      toast.error('Please allow popups to print receipts');
    }
  };

  const handleConfirmCancel = async () => {
    if (!selectedBooking || !cancelReason.trim()) {
      toast.error('Please provide a cancellation reason');
      return;
    }

    // Use dbId (actual database ID) instead of id (which might be booking reference)
    const bookingId = selectedBooking.dbId || selectedBooking.id;

    try {
      await simplBookingService.cancelBookingAdmin(bookingId, {
        reason: cancelReason,
        adminNotes: `Cancelled from bookings page by admin`
      });

      toast.success('Booking cancelled successfully!');
      setCancelDialogOpen(false);
      setCancelReason('');
      setSelectedBooking(null);
      loadBookings(); // Refresh the bookings list
    } catch (error: any) {
      console.error('Failed to cancel booking:', error);
      toast.error(error.message || 'Failed to cancel booking');
    }
  };

  const handleNewBooking = () => {
    setBookingTypeDialogOpen(true);
  };

  const handleBookingTypeSelect = (type: 'car-rental' | 'driver-hire' | 'yacht-charter') => {
    setBookingTypeDialogOpen(false);
    switch (type) {
      case 'car-rental':
        navigate('/bookings/rentals');
        break;
      case 'driver-hire':
        navigate('/bookings/driver-hires');
        break;
      case 'yacht-charter':
        navigate('/bookings/yacht-charters');
        break;
    }
  };

  const handleCreateBooking = async (bookingData: CreateBookingData) => {
    try {
      // Validate required fields
      if (!bookingData.customer.id) {
        setSnackbar({
          open: true,
          message: 'Error: Customer ID is required. Please select a customer.',
          type: 'error'
        });
        return;
      }

      if (bookingData.type === 'rental' && !bookingData.vehicle.id) {
        setSnackbar({
          open: true,
          message: 'Error: Vehicle ID is required for rental bookings.',
          type: 'error'
        });
        return;
      }

      if (bookingData.type === 'driver-hire' && !bookingData.driver?.id) {
        setSnackbar({
          open: true,
          message: 'Error: Driver ID is required for driver hire bookings.',
          type: 'error'
        });
        return;
      }

      // Transform CreateBookingData to AdminCreateBookingData
      const adminBookingData: AdminCreateBookingData = {
        userId: bookingData.customer.id,
        vehicleId: bookingData.vehicle.id,
        driverId: bookingData.driver?.id,
        startDate: bookingData.schedule.startDate?.toISOString() || '',
        endDate: bookingData.schedule.endDate?.toISOString() || '',
        pickupLocation: bookingData.schedule.pickupLocation,
        dropoffLocation: bookingData.schedule.dropoffLocation,
        totalAmount: bookingData.pricing.total || 0,
        paymentMethod: bookingData.paymentMethod || 'Bank Transfer',
        paymentStatus: bookingData.paymentStatus || 'pending',
        status: bookingData.status || 'pending',
        serviceType: bookingData.type === 'rental' ? 'self-drive' : bookingData.type,
        bookingType: 'instant',
        customerName: bookingData.customer.name,
        vehicleName: `${bookingData.vehicle.make} ${bookingData.vehicle.model}`,
        driverName: bookingData.driver?.name,
        notes: bookingData.notes
      };


      // Call the API to create the booking
      await bookingService.createAdminBooking(adminBookingData);

      setSnackbar({
        open: true,
        message: 'Booking created successfully!',
        type: 'success'
      });

      setCreateBookingOpen(false);
      await loadBookings(); // Refresh the list
    } catch (error) {
      console.error('Error creating booking:', error);
      setSnackbar({
        open: true,
        message: `Error creating booking: ${error instanceof Error ? error.message : 'Unknown error'}`,
        type: 'error'
      });
    }
  };

  const handleExport = () => {
    const csvData = bookings.map(booking => ({
      'Booking Ref': booking.bookingRef,
      'Customer': booking.customer.name,
      'Email': booking.customer.email,
      'Phone': booking.customer.phone,
      'Vehicle': `${booking.vehicle.make} ${booking.vehicle.model}`,
      'Start Date': formatDateInServiceTimezone(booking.startDate, booking.timezone, { dateStyle: 'short' }),
      'End Date': formatDateInServiceTimezone(booking.endDate, booking.timezone, { dateStyle: 'short' }),
      'Service Location': booking.serviceLocation || booking.location,
      'Timezone': booking.timezone || 'Africa/Lagos',
      'Status': booking.status,
      'Amount': `₦${booking.amount.toLocaleString()}`,
      'Duration': booking.duration
    }));

    const csvContent = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bookings_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    setSnackbar({ open: true, message: 'Bookings exported successfully!', type: 'success' });
  };

  const loadAvailableDrivers = async () => {
    try {
      const response = await driverService.getDrivers({ status: 'active' }, 1, 100);

      if (response.drivers && response.drivers.length > 0) {
        setAvailableDrivers(response.drivers);
      } else {
        setAvailableDrivers([]);
        toast.error('No active drivers available');
      }
    } catch (error) {
      console.error('Failed to load drivers:', error);
      setAvailableDrivers([]);
      toast.error('Failed to load available drivers');
    }
  };

  const handleAssignDriver = async () => {
    if (!selectedBooking || !selectedDriverId) {
      toast.error('Please select a driver');
      return;
    }

    // Use dbId (actual database ID) instead of id (which might be booking reference)
    const bookingId = selectedBooking.dbId || selectedBooking.id;

    try {
      await simplBookingService.assignDriver(
        bookingId,
        selectedDriverId,
        'Assigned via bookings page'
      );

      toast.success('Driver assigned successfully!');
      setAssignDriverOpen(false);
      setSelectedDriverId('');
      setSelectedBooking(null);
      loadBookings(); // Refresh the bookings list
    } catch (error: any) {
      console.error('Failed to assign driver:', error);
      toast.error(error.message || 'Failed to assign driver');
    }
  };

  // Filter bookings based on tab selection and other filters
  const filteredBookings = bookings.filter(booking => {
    // Safe string matching with null checks
    const matchesSearch = searchTerm === '' ||
                         (booking.bookingRef?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                         (booking.customer?.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                         (booking.customer?.email?.toLowerCase() || '').includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === 'all' || booking.status === filterStatus;
    
    // Tab filtering
    const tabFilterMap = {
      0: 'all',
      1: 'rental',
      2: 'driver-hire',
      3: 'yacht-charter'
    };
    const tabFilter = tabFilterMap[selectedTab as keyof typeof tabFilterMap] || 'all';

    // Match booking type with multiple possible values
    let matchesTab = tabFilter === 'all';
    if (!matchesTab) {
      if (tabFilter === 'rental') {
        // Match vehicle rentals (can be 'rental', 'vehicle-rental', 'self-drive', 'self_drive')
        matchesTab = booking.type === 'rental' ||
                     booking.type === 'vehicle-rental' ||
                     booking.type === 'self-drive' ||
                     booking.type === 'self_drive' ||
                     booking.serviceCategory === 'vehicle-rental' ||
                     booking.serviceCategory === 'vehicle_rental';
      } else if (tabFilter === 'driver-hire') {
        // Match driver hires
        matchesTab = booking.type === 'driver-hire' ||
                     booking.type === 'driver_hire' ||
                     booking.serviceCategory === 'driver-hire' ||
                     booking.serviceCategory === 'driver_hire';
      } else if (tabFilter === 'yacht-charter') {
        // Match yacht charters
        matchesTab = booking.type === 'yacht-charter' ||
                     booking.type === 'yacht_charter' ||
                     booking.serviceCategory === 'yacht-charter' ||
                     booking.serviceCategory === 'yacht_charter';
      } else {
        matchesTab = booking.type === tabFilter;
      }
    }
    
    return matchesSearch && matchesStatus && matchesTab;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'info';
      case 'active': return 'success';
      case 'confirmed': return 'primary';
      case 'pending': return 'warning';
      case 'completed': return 'default';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getBookingTypeIcon = (type: string) => {
    switch (type) {
      case 'rental': return <DirectionsCar />;
      case 'driver-hire': return <People />;
      case 'agent-assisted': return <Assignment />;
      default: return <DirectionsCar />;
    }
  };

  const getBookingTypeLabel = (type: string) => {
    switch (type) {
      case 'rental': return 'Car Rental';
      case 'driver-hire': return 'Driver Hire';
      case 'agent-assisted': return 'Agent Assisted';
      case 'airport-transfer': return 'Airport Transfer';
      case 'self-drive': return 'Self-Drive';
      case 'chauffeur-driven': return 'Chauffeur-Driven';
      case 'hourly': return 'Driver Hire (Hourly)';
      case 'daily': return 'Driver Hire (Daily)';
      case 'cruise': case 'trip': case 'yacht-charter': return 'Yacht Charter';
      default: return type;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const stats = {
    total: filteredBookings.length,
    active: filteredBookings.filter(b => b.status === 'active').length,
    pending: filteredBookings.filter(b => b.status === 'pending').length,
    cancelled: filteredBookings.filter(b => b.status === 'cancelled').length,
    revenue: filteredBookings.reduce((sum, b) => sum + b.amount, 0),
  };

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            Bookings Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage all your rental bookings, driver hires, and agent-assisted services
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<VerificationIcon />}
            sx={{ textTransform: 'none' }}
            onClick={() => navigate('/access-codes')}
          >
            Verification Codes
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            sx={{ textTransform: 'none' }}
            onClick={handleNewBooking}
          >
            New Booking
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Bookings
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {stats.total}
              </Typography>
              <Chip
                label="+12% from last month"
                size="small"
                color="success"
                variant="outlined"
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Active Bookings
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                {stats.active}
              </Typography>
              <Chip
                icon={<CheckCircle />}
                label="Currently active"
                size="small"
                color="success"
                variant="outlined"
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Pending Approval
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main' }}>
                {stats.pending}
              </Typography>
              <Chip
                label="Awaiting action"
                size="small"
                color="warning"
                variant="outlined"
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Cancelled Bookings
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'error.main' }}>
                {stats.cancelled}
              </Typography>
              <Chip
                icon={<Cancel />}
                label="View cancelled"
                size="small"
                color="error"
                variant="outlined"
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>
        {isSuperAdmin() && (
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total Revenue
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {formatCurrency(stats.revenue)}
                </Typography>
                <Chip
                  label="+8% from last month"
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ mt: 1 }}
                />
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Bookings Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={selectedTab} onChange={handleTabChange}>
              <Tab label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  All Bookings
                  <Badge badgeContent={bookings.length} color="primary" />
                </Box>
              } />
              <Tab label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DirectionsCar sx={{ fontSize: 20 }} />
                  Car Rentals
                  <Badge badgeContent={bookings.filter(b =>
                    b.type === 'rental' ||
                    b.type === 'vehicle-rental' ||
                    b.type === 'self-drive' ||
                    b.type === 'self_drive' ||
                    b.serviceCategory === 'vehicle-rental' ||
                    b.serviceCategory === 'vehicle_rental'
                  ).length} color="secondary" />
                </Box>
              } />
              <Tab label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <People sx={{ fontSize: 20 }} />
                  Driver Hires
                  <Badge badgeContent={bookings.filter(b =>
                    b.type === 'driver-hire' ||
                    b.type === 'driver_hire' ||
                    b.serviceCategory === 'driver-hire' ||
                    b.serviceCategory === 'driver_hire'
                  ).length} color="secondary" />
                </Box>
              } />
              <Tab label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Assignment sx={{ fontSize: 20 }} />
                  Yacht Charters
                  <Badge badgeContent={bookings.filter(b =>
                    b.type === 'yacht-charter' ||
                    b.serviceCategory === 'yacht-charter' ||
                    b.serviceCategory === 'yacht_charter'
                  ).length} color="secondary" />
                </Box>
              } />
            </Tabs>
          </Box>

          {/* Search and Filters */}
          <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search bookings..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ flexGrow: 1, maxWidth: 400 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                displayEmpty
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="confirmed">Confirmed</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
              </Select>
            </FormControl>
            
            {/* Booking Mode Filter */}
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select
                value={bookingTypeMode}
                onChange={(e) => setBookingTypeMode(e.target.value)}
                displayEmpty
              >
                <MenuItem value="all">All Booking Modes</MenuItem>
                <MenuItem value="instant">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FlashOn sx={{ fontSize: 16, color: 'success.main' }} />
                    Instant Booking
                  </Box>
                </MenuItem>
                <MenuItem value="approval-required">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Schedule sx={{ fontSize: 16, color: 'warning.main' }} />
                    Approval Required
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
            
            <IconButton onClick={loadBookings} disabled={isLoading}>
              <Refresh />
            </IconButton>
            <IconButton onClick={handleExport}>
              <Download />
            </IconButton>
          </Box>

          {/* Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Booking Ref</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Booking Mode</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Vehicle</TableCell>
                  <TableCell>Plate</TableCell>
                  <TableCell>Driver</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Deposit</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 10 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <Typography>Loading bookings...</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : filteredBookings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 10 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h6" color="text.secondary">
                          {bookings.length === 0 ? 'No bookings found' : 'No bookings match your current filters'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {bookings.length === 0 ? 
                            'When customers make bookings, they will appear here.' : 
                            'Try adjusting your search or filter criteria.'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : filteredBookings.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((booking) => (
                  <TableRow key={booking.id} hover>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {booking.bookingRef}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getBookingTypeIcon(booking.type)}
                        label={getBookingTypeLabel(booking.type)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={booking.bookingType === 'instant' ? <FlashOn sx={{ fontSize: 16 }} /> : <Schedule sx={{ fontSize: 16 }} />}
                        label={booking.bookingType === 'instant' ? 'Instant' : 'Approval Required'}
                        size="small"
                        color={booking.bookingType === 'instant' ? 'success' : 'warning'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: booking.isCorporateBooking ? 'primary.main' : 'grey.500' }}>
                          {booking.isCorporateBooking && booking.companyName
                            ? booking.companyName.charAt(0)
                            : booking.customer.name.charAt(0)
                          }
                        </Avatar>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {booking.isCorporateBooking && (
                              <Tooltip title="Corporate Account" arrow>
                                <Business sx={{ fontSize: 14, color: 'primary.main' }} />
                              </Tooltip>
                            )}
                            <Typography variant="body2">
                              {booking.isCorporateBooking && booking.companyName
                                ? booking.companyName
                                : booking.customer.name
                              }
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {booking.customer.phone && booking.customer.phone !== 'N/A'
                              ? booking.customer.phone
                              : booking.customer.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {booking.vehicle.make} {booking.vehicle.model}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {booking.vehicle.plate}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {booking.driver ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 28, height: 28 }}>
                            {booking.driver.name.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2">{booking.driver.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {booking.driver.phone}
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        <Chip
                          label="Unassigned"
                          size="small"
                          variant="outlined"
                          color="default"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {booking.duration}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateInServiceTimezone(booking.startDate, booking.timezone, { month: 'short', day: 'numeric' })} - {formatDateInServiceTimezone(booking.endDate, booking.timezone, { month: 'short', day: 'numeric' })}
                        {booking.timezone && (
                          <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
                            {getTimezoneLabel(booking.timezone, booking.serviceLocation)}
                          </Typography>
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={booking.status}
                        size="small"
                        color={getStatusColor(booking.status) as any}
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatCurrency(booking.amount)}
                      </Typography>
                      <Chip
                        label={booking.paymentStatus}
                        size="small"
                        variant="outlined"
                        color={booking.paymentStatus === 'paid' ? 'success' : 'warning'}
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const deposit = (booking as any).payment?.securityDeposit || (booking as any).securityDeposit;
                        const depositStatus = (booking as any).payment?.securityDepositStatus || (booking as any).securityDepositStatus;

                        if (!deposit) {
                          return <Typography variant="body2" color="text.secondary">-</Typography>;
                        }

                        return (
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {formatCurrency(deposit)}
                            </Typography>
                            <Chip
                              label={
                                depositStatus === 'held' ? '🟡 Held' :
                                depositStatus === 'released' ? '🟢 Released' :
                                depositStatus === 'refunded' ? '🟢 Refunded' :
                                depositStatus === 'forfeited' ? '🔴 Forfeited' :
                                'Unknown'
                              }
                              size="small"
                              variant="outlined"
                              color={
                                depositStatus === 'released' || depositStatus === 'refunded' ? 'success' :
                                depositStatus === 'held' ? 'warning' :
                                depositStatus === 'forfeited' ? 'error' :
                                'default'
                              }
                              sx={{ fontSize: '0.7rem', height: 20, mt: 0.5 }}
                            />
                          </Box>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Verification">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              setSelectedBookingId(booking.dbId || booking.id);
                              setModalInitialTab(6); // Tab 7 (Verification)
                              setAdminDetailsOpen(true);
                            }}
                          >
                            <VerificationIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Check-in/Check-out">
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => {
                              setSelectedBookingId(booking.dbId || booking.id);
                              setModalInitialTab(7); // Tab 8 (Check-in/Checkout)
                              setAdminDetailsOpen(true);
                            }}
                          >
                            <CheckinIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="More Actions">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              handleMenuOpen(e, booking);
                            }}
                          >
                            <MoreVert fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredBookings.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </CardContent>
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => selectedBooking && handleViewDetails(selectedBooking)}>
          <Visibility sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        {selectedBooking?.paymentStatus !== 'paid' && selectedBooking?.status !== 'cancelled' && (
          <MenuItem onClick={handleGeneratePaymentLink}>
            <Payment sx={{ mr: 1 }} />
            Generate Payment Link
          </MenuItem>
        )}
        <MenuItem onClick={handlePrintReceipt}>
          <Print sx={{ mr: 1 }} />
          Print Receipt
        </MenuItem>
        <MenuItem onClick={() => {
          setAssignDriverOpen(true);
          loadAvailableDrivers();
          handleMenuClose();
        }}>
          <PersonAdd sx={{ mr: 1 }} />
          {selectedBooking?.driver ? 'Reassign Driver' : 'Assign Driver'}
        </MenuItem>
        <MenuItem onClick={handleEditBooking}>
          <Edit sx={{ mr: 1 }} />
          Edit Booking
        </MenuItem>
        <MenuItem onClick={handleCancelBooking}>
          <Cancel sx={{ mr: 1 }} />
          Cancel Booking
        </MenuItem>
      </Menu>

      {/* Create Booking Dialog */}
      <CreateBookingDialog
        open={createBookingOpen}
        onClose={() => setCreateBookingOpen(false)}
        onSubmit={handleCreateBooking}
      />

      {/* Assign Driver Dialog */}
      <Dialog
        open={assignDriverOpen}
        onClose={() => {
          setAssignDriverOpen(false);
          setSelectedDriverId('');
          setSelectedBooking(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedBooking?.driver ? 'Reassign Driver' : 'Assign Driver'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Booking: <strong>{selectedBooking?.bookingRef}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Customer: <strong>{selectedBooking?.customer.name}</strong>
            </Typography>
            {selectedBooking?.driver && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Current Driver: <strong>{selectedBooking.driver.name}</strong>
              </Typography>
            )}
            <FormControl fullWidth sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Select Driver
              </Typography>
              <Select
                value={selectedDriverId}
                onChange={(e) => setSelectedDriverId(e.target.value)}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Choose a driver...
                </MenuItem>
                {availableDrivers.map((driver) => (
                  <MenuItem key={driver.id} value={driver.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 24, height: 24 }}>
                        {driver.personalInfo?.firstName?.charAt(0) || 'D'}
                      </Avatar>
                      <Box>
                        <Typography variant="body2">
                          {driver.personalInfo?.firstName} {driver.personalInfo?.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {driver.contactInfo?.phoneNumber || 'No phone'}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAssignDriverOpen(false);
              setSelectedDriverId('');
              setSelectedBooking(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAssignDriver}
            variant="contained"
            disabled={!selectedDriverId}
          >
            Assign Driver
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Modal */}
      <BookingDetailsModal
        open={viewDetailsOpen}
        onClose={handleCloseDetails}
        bookingId={selectedBooking?.id || null}
      />

      {/* Edit Booking Modal */}
      <EditBookingModal
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedBooking(null);
        }}
        bookingId={selectedBooking?.id || null}
        onSuccess={() => {
          toast.success('Booking updated successfully!');
          loadBookings();
        }}
      />

      {/* Cancel Booking Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => {
          setCancelDialogOpen(false);
          setCancelReason('');
          setSelectedBooking(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Booking</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Are you sure you want to cancel this booking?
            </Typography>
            {selectedBooking && (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Booking:</strong> {selectedBooking.bookingRef}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Customer:</strong> {selectedBooking.customer.name}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>Vehicle:</strong> {selectedBooking.vehicle.make} {selectedBooking.vehicle.model}
                </Typography>
              </>
            )}
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Cancellation Reason (Required)"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Please provide a reason for cancelling this booking..."
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCancelDialogOpen(false);
              setCancelReason('');
              setSelectedBooking(null);
            }}
          >
            Keep Booking
          </Button>
          <Button
            onClick={handleConfirmCancel}
            variant="contained"
            color="error"
            disabled={!cancelReason.trim()}
          >
            Cancel Booking
          </Button>
        </DialogActions>
      </Dialog>

      {/* Booking Type Selection Dialog */}
      <Dialog
        open={bookingTypeDialogOpen}
        onClose={() => setBookingTypeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select Booking Type</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Choose the type of booking you'd like to create
          </Typography>
          <Stack spacing={2}>
            <Button
              variant="outlined"
              size="large"
              startIcon={<DirectionsCar />}
              onClick={() => handleBookingTypeSelect('car-rental')}
              sx={{
                justifyContent: 'flex-start',
                p: 2,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'primary.light',
                  borderColor: 'primary.main',
                }
              }}
            >
              <Box sx={{ textAlign: 'left', flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Car Rental
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Create a vehicle rental booking (Flex or Premium Package)
                </Typography>
              </Box>
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<People />}
              onClick={() => handleBookingTypeSelect('driver-hire')}
              sx={{
                justifyContent: 'flex-start',
                p: 2,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'success.light',
                  borderColor: 'success.main',
                }
              }}
            >
              <Box sx={{ textAlign: 'left', flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Driver Hire
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Hire a professional driver for personal transportation
                </Typography>
              </Box>
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<DirectionsBoat />}
              onClick={() => handleBookingTypeSelect('yacht-charter')}
              sx={{
                justifyContent: 'flex-start',
                p: 2,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'info.light',
                  borderColor: 'info.main',
                }
              }}
            >
              <Box sx={{ textAlign: 'left', flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Yacht Charter
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Book a luxury yacht charter experience
                </Typography>
              </Box>
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBookingTypeDialogOpen(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Admin Booking Details Modal */}
      <AdminBookingDetailsModal
        open={adminDetailsOpen}
        onClose={() => {
          setAdminDetailsOpen(false);
          setSelectedBookingId(null);
        }}
        bookingId={selectedBookingId}
        initialTab={modalInitialTab}
        onBookingUpdated={() => {
          loadBookings();
        }}
      />

      {/* Receipt Dialog */}
      <Dialog
        open={receiptDialogOpen}
        onClose={() => {
          setReceiptDialogOpen(false);
          setSelectedBooking(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h5">Payment Receipt</Typography>
            <Typography variant="caption">PolarRide Car Rental</Typography>
          </Box>
          <Assignment sx={{ fontSize: 40, opacity: 0.7 }} />
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          {selectedBooking && (() => {
            // Get cost breakdown from payment.breakdown (new backend format) or adminNotes (legacy)
            let costBreakdown: any = null;

            // Prefer payment.breakdown from new backend format
            if ((selectedBooking as any).payment?.breakdown) {
              costBreakdown = (selectedBooking as any).payment.breakdown;
            } else if ((selectedBooking as any).costBreakdown) {
              // Check for top-level costBreakdown (some bookings may have this)
              costBreakdown = (selectedBooking as any).costBreakdown;
            } else {
              // Fallback to parsing adminNotes for legacy bookings
              try {
                const notes = (selectedBooking as any).adminNotes;
                const parsed = typeof notes === 'string' ? JSON.parse(notes) : notes;
                costBreakdown = parsed?.costBreakdown;
                if (costBreakdown) {
                }
              } catch (e) {
                console.error('❌ Failed to parse adminNotes:', e);
              }
            }

            if (!costBreakdown) {
              console.warn('⚠️ No cost breakdown available for booking:', selectedBooking.bookingRef);
            }

            // ✅ Validate cost breakdown per API contract
            // Frontend should NEVER recalculate values, but should validate them
            if (costBreakdown) {
              const validation = validateCostBreakdown(costBreakdown);

              if (!validation.isValid) {
                logValidationErrors(validation, costBreakdown, selectedBooking.bookingRef);

                // Check if this is the old 0.1% bug
                if (hasOldBookingFeeBug(costBreakdown)) {
                  console.error(
                    '🐛 This booking has the old 0.1% booking fee bug. ' +
                    'Contact backend team to recalculate this booking.'
                  );
                }
              }
            }

            // Calculate service fee if not provided (backend is source of truth, only calculate if missing)
            if (costBreakdown && !costBreakdown.serviceFee && costBreakdown.rentalCost) {
              const serviceFeeRate = costBreakdown.serviceFeeRate || 15;
              costBreakdown.serviceFee = Math.round(costBreakdown.rentalCost * (serviceFeeRate / 100));
            }

            // Calculate host payout if not provided
            if (costBreakdown && !costBreakdown.hostPayout && costBreakdown.rentalCost && costBreakdown.serviceFee) {
              costBreakdown.hostPayout = costBreakdown.rentalCost - costBreakdown.serviceFee;
            }

            return (
              <Box id="receipt-content">
                {/* Booking Info */}
                <Grid container spacing={2} sx={{ mb: 3, p: 2, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Booking ID</Typography>
                    <Typography variant="body1" fontWeight="bold">{selectedBooking.bookingRef}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Date</Typography>
                    <Typography variant="body1">{new Date().toLocaleDateString()}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Customer</Typography>
                    <Typography variant="body1">{selectedBooking.customer?.name || 'N/A'}</Typography>
                    <Typography variant="caption" display="block" color="text.secondary">{selectedBooking.customer?.email}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Vehicle</Typography>
                    <Typography variant="body1">{selectedBooking.vehicle?.make} {selectedBooking.vehicle?.model}</Typography>
                    {selectedBooking.vehicle?.plate && selectedBooking.vehicle.plate !== 'N/A' && (
                      <Typography variant="caption" display="block" color="text.secondary">{selectedBooking.vehicle.plate}</Typography>
                    )}
                  </Grid>
                </Grid>

                {/* Rental Period */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Rental Period</Typography>
                  <Typography variant="body2">
                    {formatDateInServiceTimezone(selectedBooking.startDate, selectedBooking.timezone, { dateStyle: 'medium' })} to {formatDateInServiceTimezone(selectedBooking.endDate, selectedBooking.timezone, { dateStyle: 'medium' })}
                  </Typography>
                  <Typography variant="caption" display="block">Duration: {selectedBooking.duration}</Typography>
                </Box>

                {/* Airport Transfer Details - Show only for airport bookings */}
                {((selectedBooking as any).serviceType?.toLowerCase().includes('airport') ||
                  (selectedBooking as any).tripType ||
                  (selectedBooking as any).airport ||
                  (selectedBooking as any).zone) && (() => {
                  // Log the booking data to diagnose missing fields

                  return (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', border: '1px solid', borderColor: 'primary.main', borderRadius: 1 }}>
                      <Typography variant="subtitle2" color="primary" gutterBottom fontWeight="bold">
                        Airport Transfer Details
                      </Typography>
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        {/* Airport - Always show for airport transfers */}
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">Airport</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {(selectedBooking as any).airport?.name ||
                             (selectedBooking as any).airport?.airportName ||
                             selectedBooking.serviceLocation ||
                             'Not specified'}
                          </Typography>
                          {((selectedBooking as any).airport?.location || selectedBooking.location) && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              {(selectedBooking as any).airport?.location || selectedBooking.location}
                            </Typography>
                          )}
                        </Grid>

                        {/* Trip Type */}
                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" color="text.secondary">Trip Type</Typography>
                          <Typography variant="body2">
                            {(selectedBooking as any).tripType === 'airport_pickup' ? 'Airport Pickup' :
                             (selectedBooking as any).tripType === 'airport_dropoff' ? 'Airport Dropoff' :
                             (selectedBooking as any).serviceType?.includes('pickup') ? 'Airport Pickup' :
                             (selectedBooking as any).serviceType?.includes('dropoff') ? 'Airport Dropoff' :
                             'Airport Transfer'}
                          </Typography>
                        </Grid>

                        {/* Zone - Always show for airport transfers */}
                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" color="text.secondary">Customer Zone</Typography>
                          <Typography variant="body2">
                            {(selectedBooking as any).zone?.name ||
                             (selectedBooking as any).zone?.zoneName ||
                             (selectedBooking as any).pickupZone ||
                             (selectedBooking as any).dropoffZone ||
                             'Not specified'}
                          </Typography>
                        </Grid>
                        {(selectedBooking as any).pickupLocation && (
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">
                              {(selectedBooking as any).tripType === 'airport_dropoff' ? 'Pickup Address' : 'Pickup Location'}
                            </Typography>
                            <Typography variant="body2">{(selectedBooking as any).pickupLocation}</Typography>
                          </Grid>
                        )}
                        {(selectedBooking as any).dropoffLocation && (
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">
                              {(selectedBooking as any).tripType === 'airport_pickup' ? 'Dropoff Address' : 'Dropoff Location'}
                            </Typography>
                            <Typography variant="body2">{(selectedBooking as any).dropoffLocation}</Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  </>
                  );
                })()}

                <Divider sx={{ my: 2 }} />

                {/* Cost Breakdown */}
                <Typography variant="h6" sx={{ mb: 2 }}>Cost Breakdown</Typography>

                {/* ⚠️ Validation Warning Banner */}
                {costBreakdown && hasOldBookingFeeBug(costBreakdown) && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight="bold">
                      ⚠️ Cost Calculation Issue Detected
                    </Typography>
                    <Typography variant="caption" display="block">
                      This booking may have incorrect fee calculations (old 0.1% bug instead of 5%).
                      Please contact support to recalculate this booking.
                    </Typography>
                  </Alert>
                )}

                {costBreakdown ? (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Customer Pays:</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Rental Cost</Typography>
                      <Typography variant="body2">₦{costBreakdown.rentalCost?.toLocaleString() || '0'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Booking Fee ({costBreakdown.bookingFeeRate || 5}%)</Typography>
                      <Typography variant="body2">₦{costBreakdown.bookingFee?.toLocaleString() || '0'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">VAT ({costBreakdown.vatRate || 7.5}%)</Typography>
                      <Typography variant="body2">₦{costBreakdown.vat?.toLocaleString() || '0'}</Typography>
                    </Box>
                    {costBreakdown.securityDeposit && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="warning.main">Security Deposit (Refundable)</Typography>
                        <Typography variant="body2" color="warning.main">₦{costBreakdown.securityDeposit?.toLocaleString() || '0'}</Typography>
                      </Box>
                    )}
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, bgcolor: 'primary.main', color: 'primary.contrastText', p: 1.5, borderRadius: 1 }}>
                      <Typography variant="h6" sx={{ color: 'inherit' }}>Total Amount Paid</Typography>
                      <Typography variant="h6" sx={{ color: 'inherit', fontWeight: 'bold' }}>₦{costBreakdown.totalAmount?.toLocaleString() || selectedBooking.amount?.toLocaleString() || '0'}</Typography>
                    </Box>

                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="caption">
                        Platform service fee ({costBreakdown.serviceFeeRate || 15}%: ₦{costBreakdown.serviceFee?.toLocaleString() || '0'}) is deducted from host payout
                      </Typography>
                    </Alert>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', bgcolor: 'success.main', color: 'success.contrastText', p: 1.5, borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ color: 'inherit' }}>Host Receives</Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: 'inherit' }}>
                        ₦{costBreakdown.hostPayout?.toLocaleString() || '0'}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Alert severity="warning">
                    Cost breakdown not available for this booking.
                  </Alert>
                )}

                <Divider sx={{ my: 3 }} />

                {/* Payment Status */}
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">Payment Status</Typography>
                  <Chip
                    label={selectedBooking.paymentStatus || 'Pending'}
                    color={selectedBooking.paymentStatus === 'paid' ? 'success' : 'warning'}
                    sx={{ mt: 1 }}
                  />
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3, textAlign: 'center' }}>
                  Thank you for choosing PolarRide!
                </Typography>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={() => {
              setReceiptDialogOpen(false);
              setSelectedBooking(null);
            }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<Print />}
            onClick={handlePrint}
          >
            Print Receipt
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Link Modal */}
      <Dialog
        open={paymentLinkDialogOpen}
        onClose={() => setPaymentLinkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {paymentLinkData?.isExisting ? (
              <>
                <LinkIcon color="primary" />
                <Typography variant="h6">Existing Payment Link Retrieved</Typography>
              </>
            ) : (
              <>
                <CheckCircle color="success" />
                <Typography variant="h6">Payment Link Generated</Typography>
              </>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {paymentLinkData && (
            <Stack spacing={3} sx={{ mt: 1 }}>
              {/* Customer Info */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Customer Details
                </Typography>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Name:</Typography>
                      <Typography variant="body2" fontWeight={600}>{paymentLinkData.customerName}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Email:</Typography>
                      <Typography variant="body2">{paymentLinkData.customerEmail}</Typography>
                    </Box>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">Amount:</Typography>
                      <Typography variant="h6" color="primary.main" fontWeight={700}>
                        ₦{paymentLinkData.amount.toLocaleString()}
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              </Box>

              {/* Payment Link */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Payment Link
                </Typography>
                <Card variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Stack spacing={1}>
                    <Typography
                      variant="body2"
                      sx={{
                        wordBreak: 'break-all',
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        color: 'primary.main'
                      }}
                    >
                      {paymentLinkData.paymentUrl}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ContentCopy />}
                      onClick={async () => {
                        await navigator.clipboard.writeText(paymentLinkData.paymentUrl);
                        toast.success('Link copied to clipboard!');
                      }}
                      fullWidth
                    >
                      Copy Link
                    </Button>
                  </Stack>
                </Card>
              </Box>

              {/* Instructions */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Next Steps
                </Typography>
                <Stack spacing={1}>
                  <Chip
                    icon={<WhatsApp />}
                    label={paymentLinkData.instructions?.share || 'Share this link with the customer via WhatsApp, SMS, or Email'}
                    variant="outlined"
                    color="success"
                    sx={{ justifyContent: 'flex-start', height: 'auto', py: 1.5, px: 1 }}
                  />
                  <Chip
                    icon={<Schedule />}
                    label={paymentLinkData.instructions?.validity || 'Link is valid until payment is completed'}
                    variant="outlined"
                    color="info"
                    sx={{ justifyContent: 'flex-start', height: 'auto', py: 1.5, px: 1 }}
                  />
                  <Chip
                    icon={<CheckCircle />}
                    label={paymentLinkData.instructions?.webhook || 'Payment status will update automatically when customer pays'}
                    variant="outlined"
                    color="primary"
                    sx={{ justifyContent: 'flex-start', height: 'auto', py: 1.5, px: 1 }}
                  />
                </Stack>
              </Box>

              {/* Quick Actions */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Quick Share
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<WhatsApp />}
                    color="success"
                    onClick={() => {
                      const message = `Hi ${paymentLinkData.customerName}, here's your payment link for ₦${paymentLinkData.amount.toLocaleString()}: ${paymentLinkData.paymentUrl}`;
                      window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
                    }}
                  >
                    WhatsApp
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Email />}
                    onClick={() => {
                      const subject = 'PolarRide Payment Link';
                      const body = `Hi ${paymentLinkData.customerName},\n\nPlease complete your payment of ₦${paymentLinkData.amount.toLocaleString()} using this link:\n\n${paymentLinkData.paymentUrl}\n\nThank you!`;
                      window.open(`mailto:${paymentLinkData.customerEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
                    }}
                  >
                    Email
                  </Button>
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setPaymentLinkDialogOpen(false)} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
};

export default EnhancedAllBookingsPage;